import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { Home, Compass, Target, MessageCircle, User, Clock, Sparkles, BookOpen } from "lucide-react";
import { WinCardHost } from "@/components/WinCard";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/Logo";
import { LangToggle } from "@/components/LangToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { ContactLinks } from "@/components/ContactLinks";

const items = [
  { to: "/app/home", k: "navHome", icon: Home, id: "nav-home" },
  { to: "/app/career", k: "navCareer", icon: Compass, id: "nav-career" },
  { to: "/app/missions", k: "navMissions", icon: Target, id: "nav-missions" },
  { to: "/app/upskill", k: "navUpskill", icon: Sparkles, id: "nav-upskill" },
  { to: "/app/coach", k: "navCoach", icon: MessageCircle, id: "nav-coach" },
  { to: "/app/profile", k: "navProfile", icon: User, id: "nav-profile" },
];

export function AppShell() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const isPlus = user?.plan === "plus" || ["admin", "super_admin"].includes(user?.role);

  useEffect(() => {
    if (isPlus) { setPending(false); return; }
    api.get("/payments/status").then(({ data }) => setPending(!!data.pending)).catch(() => {});
  }, [isPlus]);

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* top bar */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 md:px-6">
          <button onClick={() => navigate("/app/home")} data-testid="header-logo-btn">
            <Logo />
          </button>
          <div className="flex items-center gap-2">
            <LangToggle className="hidden sm:inline-flex" />
            <button data-testid="header-pro-preview" onClick={() => navigate("/pro")} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-900 transition-colors hover:bg-amber-100">Pro preview</button>
            <button data-testid="header-notebook" aria-label="Open private Career Notebook" title="Career Notebook" onClick={() => navigate("/app/notebook")} className="rounded-full p-2 text-slate-600 transition-colors hover:bg-amber-50 hover:text-amber-800"><BookOpen className="h-4 w-4" /></button>
            <NotificationBell />
            {isPlus ? (
              <Badge className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white border-0" data-testid="plus-badge">
                {user?.pro_access ? "NxT Pro" : `⚡ ${user?.xp ?? 0} XP`}
              </Badge>
            ) : (
              <button
                onClick={() => navigate("/paywall")}
                data-testid="upgrade-header-btn"
                className="rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-indigo-600"
              >
                {t("upgrade")}
              </button>
            )}
          </div>
        </div>
        <ContactLinks testId="app-header" className="mx-auto max-w-3xl px-4 pb-2 md:px-6" />
      </header>

      <main className="mx-auto max-w-3xl px-4 py-5 md:px-6 md:py-8">
        {pending && !isPlus && (
          <div data-testid="pending-activation-banner" className="mb-4 flex items-center gap-2 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs font-bold text-amber-800">
            <Clock className="h-4 w-4 shrink-0" /> {t("pendingBanner")}
          </div>
        )}
        <Outlet />
      </main>
      <WinCardHost />

      {/* bottom nav (mobile) + side-ish on desktop centered */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/60 bg-background/90 backdrop-blur-lg md:mx-auto md:max-w-3xl md:rounded-t-2xl md:border-x">
        <div className="mx-auto flex max-w-3xl items-stretch justify-around px-2">
          {items.map(({ to, k, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={id}
              className={({ isActive }) =>
                cn(
                  "group flex flex-1 flex-col items-center gap-1 py-2.5 text-[11px] font-semibold transition-colors",
                  isActive ? "text-indigo-600" : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-xl transition-all",
                      isActive ? "bg-indigo-600/10" : "group-hover:bg-secondary"
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={isActive ? 2.5 : 2} />
                  </span>
                  {t(k)}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
